import { useState } from 'react';
import { useT } from '@/store';
import { useSaveVehicle, VEHICLE_STATUSES, VEHICLE_TYPES } from '@/hooks/useFleet';
import { Modal } from '@/components/ui/Overlay';
import { ErrorState } from '@/components/ui/Panel';

/** keliumokestis.lt kainoraščio ašys. Kodai turi sutapti su
 *  data/lakd/vignette_rates.json ir su vehicles.toll_category CHECK'u —
 *  backend'as tikrina pagal patį kainoraštį, todėl neteisingas kodas
 *  grįžta kaip 400, o ne kaip tyliai neįrašytas laukas. */
const TOLL_CATEGORIES = ['N1', 'N2', 'N3', 'M2', 'M3'];
const EURO_CLASSES = ['EURO_0', 'EURO_I', 'EURO_II', 'EURO_III', 'EURO_IV', 'EURO_V', 'EURO_VI'];

/**
 * Add / edit one vehicle.
 *
 * The assigned driver is deliberately absent from this form. The backend
 * refuses `current_driver_id` on PUT — every driver change has to go through
 * assign-driver so vehicle_assignments_history stays the single record of
 * who drove what — and a field that silently works on create but 400s on
 * edit is worse than no field at all. Reassignment stays on the card, where
 * it already is.
 *
 * Dates are native date inputs so the value the API receives is always
 * 'YYYY-MM-DD' — the exact format the compliance filter's SQL date()
 * comparison expects, and the one thing the backend rejects outright.
 */
export function VehicleFormModal({ vehicle, onClose }) {
  const t = useT();
  const saveVehicle = useSaveVehicle();
  const [form, setForm] = useState(() => toForm(vehicle));
  const isEdit = Boolean(vehicle?.vehicle_id);
  const set = (patch) => setForm((current) => ({ ...current, ...patch }));

  const submit = () => {
    const plate = form.license_plate.trim();
    if (!plate) return;

    saveVehicle.mutate(
      {
        vehicleId: vehicle?.vehicle_id,
        license_plate: plate,
        vehicle_type: form.vehicle_type,
        status: form.status,
        make_model: form.make_model.trim() || null,
        vin: form.vin.trim() || null,
        manufacture_year: toNumberOrNull(form.manufacture_year),
        capacity_tons: toNumberOrNull(form.capacity_tons),
        odometer_km: toNumberOrNull(form.odometer_km),
        tech_inspection_expiry: form.tech_inspection_expiry || null,
        insurance_civil_expiry: form.insurance_civil_expiry || null,
        insurance_kasko_expiry: form.insurance_kasko_expiry || null,
        active_vignette_until: vignetteToIso(form.active_vignette_until),
        toll_category: form.toll_category || null,
        euro_class: form.euro_class || null,
        axle_count: toNumberOrNull(form.axle_count),
      },
      { onSuccess: onClose },
    );
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={t(isEdit ? 'transportas.editVehicle' : 'transportas.addVehicle')}
      width="max-w-[620px]"
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>
            {t('common.cancel')}
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={submit}
            disabled={saveVehicle.isPending || !form.license_plate.trim()}
          >
            {t('common.saveBtn')}
          </button>
        </>
      }
    >
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <Field label={t('transportas.col.plate')}>
            <input
              className="field"
              value={form.license_plate}
              placeholder={t('transportas.platePh')}
              onChange={(e) => set({ license_plate: e.target.value })}
            />
          </Field>
          <Field label={t('transportas.col.type')}>
            <select
              className="field"
              value={form.vehicle_type}
              onChange={(e) => set({ vehicle_type: e.target.value })}
            >
              {VEHICLE_TYPES.map((code) => (
                <option key={code} value={code}>
                  {t(`transportas.type.${code}`)}
                </option>
              ))}
            </select>
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label={t('transportas.col.makeModel')}>
            <input
              className="field"
              value={form.make_model}
              placeholder={t('transportas.makeModelPh')}
              onChange={(e) => set({ make_model: e.target.value })}
            />
          </Field>
          <Field label={t('transportas.col.status')}>
            <select className="field" value={form.status} onChange={(e) => set({ status: e.target.value })}>
              {VEHICLE_STATUSES.map((code) => (
                <option key={code} value={code}>
                  {t(`transportas.status.${code}`)}
                </option>
              ))}
            </select>
          </Field>
        </div>

        <div className="grid grid-cols-3 gap-3">
          <Field label="VIN">
            <input
              className="field"
              value={form.vin}
              placeholder={t('transportas.vinPh')}
              onChange={(e) => set({ vin: e.target.value })}
            />
          </Field>
          <Field label={t('transportas.yearPh')}>
            <input
              className="field"
              type="number"
              inputMode="numeric"
              min="1970"
              max={new Date().getFullYear() + 1}
              value={form.manufacture_year}
              onChange={(e) => set({ manufacture_year: e.target.value })}
            />
          </Field>
          <Field label={t('transportas.odometerPh')}>
            <input
              className="field"
              type="number"
              inputMode="numeric"
              min="0"
              value={form.odometer_km}
              onChange={(e) => set({ odometer_km: e.target.value })}
            />
          </Field>
        </div>

        <div className="grid grid-cols-3 gap-3">
          {/* How much this unit carries. Left blank it stays blank — the
              dispatcher then gets no overload warning for this truck, which
              is the honest outcome: a default here would be the system
              stating a capacity nobody entered. */}
          <Field label={t('transportas.capacityPh')} hint={t('transportas.capacityHint')}>
            <input
              className="field"
              type="number"
              inputMode="decimal"
              min="0"
              step="0.5"
              value={form.capacity_tons}
              onChange={(e) => set({ capacity_tons: e.target.value })}
            />
          </Field>
        </div>

        <div className="grid grid-cols-3 gap-3">
          <Field label={t('transportas.techInspection')}>
            <input
              className="field"
              type="date"
              value={form.tech_inspection_expiry}
              onChange={(e) => set({ tech_inspection_expiry: e.target.value })}
            />
          </Field>
          <Field label={t('transportas.insuranceCivil')}>
            <input
              className="field"
              type="date"
              value={form.insurance_civil_expiry}
              onChange={(e) => set({ insurance_civil_expiry: e.target.value })}
            />
          </Field>
          <Field label={t('transportas.insuranceKasko')}>
            <input
              className="field"
              type="date"
              value={form.insurance_kasko_expiry}
              onChange={(e) => set({ insurance_kasko_expiry: e.target.value })}
            />
          </Field>
          {/* datetime-local, not date, unlike the three above: a day
              vignette expires at a specific hour, and rounding it to a date
              would hand the driver up to 23 hours of cover they do not
              have. */}
          <Field label={t('transportas.vignetteUntil')}>
            <input
              className="field"
              type="datetime-local"
              value={form.active_vignette_until}
              onChange={(e) => set({ active_vignette_until: e.target.value })}
            />
          </Field>
        </div>

        {/* Kelių mokesčio kainoraščio ašys. Kartu jos nustato tikslų tarifą
            (13-51 EUR už parą priklausomai nuo kategorijos, EURO klasės ir
            ašių) vietoj vienos vidutinės tenanto reikšmės. Palikus tuščius —
            grįžtama į tą numatytąją reikšmę, o ne spėjamas tarifas. */}
        <div className="grid grid-cols-3 gap-3">
          <Field label={t('transportas.tollCategory')}>
            <select
              className="field"
              value={form.toll_category}
              onChange={(e) => set({ toll_category: e.target.value })}
            >
              <option value="">{t('transportas.tollNotSet')}</option>
              {TOLL_CATEGORIES.map((code) => (
                <option key={code} value={code}>
                  {t(`transportas.tollCategory.${code}`)}
                </option>
              ))}
            </select>
          </Field>
          <Field label={t('transportas.euroClass')}>
            <select
              className="field"
              value={form.euro_class}
              onChange={(e) => set({ euro_class: e.target.value })}
            >
              <option value="">{t('transportas.tollNotSet')}</option>
              {EURO_CLASSES.map((code) => (
                <option key={code} value={code}>
                  {code.replace('EURO_', 'EURO ')}
                </option>
              ))}
            </select>
          </Field>
          <Field label={t('transportas.axleCount')}>
            <input
              className="field"
              type="number"
              inputMode="numeric"
              min="2"
              max="12"
              value={form.axle_count}
              onChange={(e) => set({ axle_count: e.target.value })}
            />
          </Field>
        </div>
        <p className="text-[10.5px] leading-snug text-dim">{t('transportas.tollHint')}</p>

        {saveVehicle.isError ? <ErrorState error={saveVehicle.error} /> : null}
      </div>
    </Modal>
  );
}

/** API row -> form state. Every field is a string, because that is what an
 *  input holds; nulls become '' so React never flips a controlled input to
 *  uncontrolled halfway through an edit. */
function toForm(vehicle) {
  return {
    license_plate: vehicle?.license_plate ?? '',
    vehicle_type: vehicle?.vehicle_type ?? 'dump_truck',
    status: vehicle?.status ?? 'active',
    make_model: vehicle?.make_model ?? '',
    vin: vehicle?.vin ?? '',
    manufacture_year: vehicle?.manufacture_year == null ? '' : String(vehicle.manufacture_year),
    capacity_tons: vehicle?.capacity_tons == null ? '' : String(vehicle.capacity_tons),
    odometer_km: vehicle?.odometer_km == null ? '' : String(vehicle.odometer_km),
    tech_inspection_expiry: vehicle?.tech_inspection_expiry ?? '',
    insurance_civil_expiry: vehicle?.insurance_civil_expiry ?? '',
    insurance_kasko_expiry: vehicle?.insurance_kasko_expiry ?? '',
    active_vignette_until: isoToLocalInput(vehicle?.active_vignette_until),
    toll_category: vehicle?.toll_category ?? '',
    euro_class: vehicle?.euro_class ?? '',
    axle_count: vehicle?.axle_count == null ? '' : String(vehicle.axle_count),
  };
}

/** ISO-8601 UTC (what the backend stores) -> the `YYYY-MM-DDTHH:mm` a
 *  datetime-local input accepts, in the browser's own timezone. Going
 *  through Date rather than slicing the string is the point: a dispatcher in
 *  Vilnius entering "18:00" means 18:00 local, and a naive slice would show
 *  them the UTC hour and quietly move every expiry by the offset. */
function isoToLocalInput(iso) {
  if (!iso) return '';
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return (
    `${parsed.getFullYear()}-${pad(parsed.getMonth() + 1)}-${pad(parsed.getDate())}` +
    `T${pad(parsed.getHours())}:${pad(parsed.getMinutes())}`
  );
}

/** The reverse: local wall-clock back to the ISO instant the backend and
 *  core/routing.py compare against. '' clears the column. */
function vignetteToIso(value) {
  if (!String(value).trim()) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed.toISOString();
}

/** '' means "no value" and clears the column. Anything unparseable is sent
 *  as null too — the number inputs already reject it, and null is the one
 *  value the backend treats as "unset" rather than as a bad number. */
function toNumberOrNull(value) {
  if (String(value).trim() === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function Field({ label, hint, children }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-[10.5px] font-bold uppercase tracking-[0.03em] text-dim">{label}</span>
      {children}
      {/* Under the input, not in a tooltip: what an empty field DOES is the
          part a carrier has to know before deciding to leave it empty. */}
      {hint ? <span className="mt-1 block text-[10.5px] leading-snug text-dim">{hint}</span> : null}
    </label>
  );
}
